/** @format */
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { useTranslation } from 'next-i18next';
import React, { Fragment, FunctionComponent } from 'react';
import styled from 'styled-components';

/*
 * The lit bulb — the same icon the model page hero's insights action uses,
 * for the same reason: a bulb with its light on is what reads as an
 * insight.
 */
import LightbulbActiveIcon from '@toucan/client/global/icons/lightbulb-active.svg';

import { MediaQuery, useMediaQuery } from '@toucan/client/global/application';

/*
 * The repo's own tooltip hook rather than the Feather one it defers to, because
 * Feather's reaches for document.body while the hook runs and this page is
 * server-rendered — it throws before it can render anything. The one here guards
 * that behind a window check, which is why the model page's hero uses it too. Do
 * not follow its deprecation notice without checking that first.
 */
import { useTooltip } from '@toucan/client/shared/hooks/use-tooltip';

import { DEFAULT_TEST_ID, TOOLTIP_ID } from './config/constants';
import { InsightsActionProps } from './config/types';
import { styles } from './insights-action.styles';

export * from './config/constants';
export type { InsightsActionProps } from './config/types';

const StyledInsightsAction = styled.button`
    ${styles}
`;

/**
 * The product page's own way in to the insights sections: an icon at the
 * end of the product name, with no label under it.
 *
 * The model page's equivalent action carries the words "Model insights"
 * because it sits in a row of several actions that all need naming. Here
 * there is nothing else beside it to distinguish it from, so the icon alone
 * is enough — and a label would sit awkwardly hanging off the end of a
 * product name rather than in a row built for it. The lack of visible text
 * is why an accessible name is given directly rather than left to on-screen
 * copy.
 *
 * On a pointer-sized screen the same words the model page prints under its
 * icon are given on hover instead. The tooltip is desktop-only, and left out
 * rather than merely hidden below that width: it opens on hover and focus,
 * neither of which a touch reader has a way to ask for without also
 * activating the button, so on a phone it would only ever be a bubble that
 * appears as the page scrolls away underneath it.
 *
 * Nothing is lost by leaving it out, because the accessible name on the icon
 * carries the same meaning to a screen reader at every width.
 */
export const InsightsAction: FunctionComponent<InsightsActionProps> = ({
    onClick,
    className,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
}: InsightsActionProps) => {
    const { t } = useTranslation();
    const isDesktop = useMediaQuery(MediaQuery.DESKTOP);

    const [bindTooltip, Tooltip] = useTooltip<HTMLButtonElement>({
        id: TOOLTIP_ID,
    });

    /*
     * The hover and focus handlers are only bound where the tooltip they open is
     * rendered, so below desktop this is an ordinary button with nothing
     * listening — rather than one that opens a tooltip nothing draws.
     */
    const tooltipProps = isDesktop ? bindTooltip : {};

    return (
        <Fragment>
            <StyledInsightsAction
                type="button"
                className={className}
                onClick={onClick}
                data-testid={dataTestId}
                {...tooltipProps}
            >
                <Icon
                    variant={IconVariant.ACTIONABLE_SECONDARY}
                    size={IconSize.M}
                    aria-label={t('See how customers buy this model')}
                >
                    <LightbulbActiveIcon />
                </Icon>
            </StyledInsightsAction>
            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {isDesktop && (
                <Tooltip>
                    <span data-testid={`${dataTestId}__tooltip`}>
                        {t('Model insights')}
                    </span>
                </Tooltip>
            )}
        </Fragment>
    );
};
